import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TimeAvailability } from "@/components/TimeAvailability";
import { PhoneVerification } from "@/components/PhoneVerification";
import { SchoolSelect } from "@/components/SchoolSelect";
import {
  MENTOR_GRADES,
  MENTOR_SCHOOL_OPTIONS,
  GENDER_OPTIONS,
  validateMentorForm,
  type MentorFormData,
} from "@/lib/formSchema";
import { ArrowLeft, Loader2 } from "lucide-react";

interface MentorFormProps {
  onBack: () => void;
  onSuccess: () => void;
}

export function MentorForm({ onBack, onSuccess }: MentorFormProps) {
  const [formData, setFormData] = useState<Partial<MentorFormData>>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    grade: "",
    gender: "",
    school: "",
    timesAvailable: [],
    smsOptIn: false,
  });
  const [confirmEmail, setConfirmEmail] = useState("");
  const [errors, setErrors] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);

  const updateField = <K extends keyof MentorFormData>(field: K, value: MentorFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors([]); // Clear errors on change
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validationErrors = validateMentorForm(formData);
    if (formData.email && confirmEmail !== formData.email) {
      validationErrors.push("Email addresses do not match");
    }
    if (!phoneVerified) {
      validationErrors.push("Please verify your phone number");
    }
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors([]);

    try {
      const response = await fetch("/api/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "mentor", ...formData }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to submit form");
      }

      onSuccess();
    } catch (error) {
      setErrors([error instanceof Error ? error.message : "Failed to submit form. Please try again."]);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-8 px-4">
      <div className="container mx-auto max-w-2xl">
        <Button variant="ghost" onClick={onBack} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl text-blue-900">Mentor Sign Up</CardTitle>
            <CardDescription>
              Join Educational Justice as a mentor and help guide the next generation of scholars.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {errors.length > 0 && (
                <div className="rounded-md bg-destructive/10 border border-destructive/20 p-4">
                  <ul className="list-disc list-inside space-y-1 text-sm text-destructive">
                    {errors.map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    value={formData.firstName}
                    onChange={(e) => updateField("firstName", e.target.value)}
                    placeholder="Enter your first name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    value={formData.lastName}
                    onChange={(e) => updateField("lastName", e.target.value)}
                    placeholder="Enter your last name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => updateField("email", e.target.value)}
                  placeholder="your.email@example.com"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmEmail">Confirm Email</Label>
                <Input
                  id="confirmEmail"
                  type="email"
                  value={confirmEmail}
                  onChange={(e) => {
                    setConfirmEmail(e.target.value);
                    setErrors([]);
                  }}
                  placeholder="Re-enter your email"
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="grade">Current Grade</Label>
                  <Select value={formData.grade} onValueChange={(value) => updateField("grade", value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select grade" />
                    </SelectTrigger>
                    <SelectContent>
                      {MENTOR_GRADES.map((grade) => (
                        <SelectItem key={grade} value={grade}>
                          {grade} Grade
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select value={formData.gender} onValueChange={(value) => updateField("gender", value)}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select gender" />
                    </SelectTrigger>
                    <SelectContent>
                      {GENDER_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="school">School</Label>
                <SchoolSelect
                  value={formData.school || ""}
                  onChange={(value) => updateField("school", value)}
                  options={MENTOR_SCHOOL_OPTIONS}
                  placeholder="Search for your school..."
                />
              </div>

              <TimeAvailability
                value={formData.timesAvailable || []}
                onChange={(value) => updateField("timesAvailable", value)}
              />

              <PhoneVerification
                phone={formData.phone || ""}
                onPhoneChange={(phone) => updateField("phone", phone)}
                onVerified={setPhoneVerified}
                isVerified={phoneVerified}
                accentColor="blue"
              />

              <div className="flex items-start space-x-3 rounded-md border p-4">
                <Checkbox
                  id="smsOptIn"
                  checked={formData.smsOptIn}
                  onCheckedChange={(checked) => updateField("smsOptIn", checked)}
                />
                <label
                  htmlFor="smsOptIn"
                  className="text-sm text-muted-foreground leading-relaxed cursor-pointer"
                >
                  I agree to receive text messages from Educational Justice at the phone number provided. Messages may include mentoring communications, reminders, and program updates. Message frequency varies. Message & data rates may apply. Reply STOP to opt out.
                </label>
              </div>

              <p className="text-sm text-muted-foreground text-center">
                By signing up, you agree to our{" "}
                <a
                  href="https://educationaljustice.org/privacy-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Privacy Policy
                </a>{" "}
                and{" "}
                <a
                  href="https://educationaljustice.org/terms-of-service/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Terms of Service
                </a>
                .
              </p>

              <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Sign Up as Mentor"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
